import classNames from "classnames";
import Link from "next/link";
import * as React from "react";
import { useMemo } from "react";
import { ImageFlux } from "@components/ImageFlux";
import ShopCampaignLabel from "@components/ShopCampaignLabel";
import type { ShopMatchReception_ShopMatchFragment } from "@components/ShopMatchReception/__generated__/fragments";
import ShopRating from "@components/ShopRating";
import { Text } from "@components/Text";
import { paths } from "@libs/paths";
import MatchBadge from "../MatchBadge";
import { ShopCardLargeFooter } from "./ShopCardLargeFooter";
import styles from "./index.module.css";
import type { ShopCardLarge_ShopFragment as ShopListItemFragment } from "./__generated__/fragments";

type Props = {
  shop: ShopListItemFragment;
  shopMatch?: ShopMatchReception_ShopMatchFragment;
  headingTag?: boolean;
  onClick?: () => void;
};

export const ShopCardLarge: React.FC<Props> = ({
  shop,
  shopMatch,
  headingTag,
  onClick,
}) => {
  const match = shopMatch?.match;

  const firstImage = shop.thumbnailImages[0];
  const imagePath = firstImage ? firstImage.imageUrl : "";

  // カレンダーの描画にコストがかかるためメモ化する
  // cf. https://github.com/mixi-sb/fansta-doc/issues/2815
  const Footer = useMemo(
    () => <ShopCardLargeFooter shop={shop} shopMatch={shopMatch} />,
    [shop]
  );

  return (
    <div
      key={shop.uuid}
      className={classNames(styles.listItem)}
      onClick={onClick}
    >
      <Link
        href={paths.shopDetail(shop.uuid)}
        className={styles.backgroundLink}
      ></Link>
      <div className={styles.card}>
        <dl className={styles.detail}>
          <dd className={classNames(styles.detailImage)}>
            <span className={styles.detailImageWrap}>
              <ImageFlux imageType={"shop"} url={imagePath} alt={""} />
            </span>

            {match && <MatchBadge match={match} />}
          </dd>
          <dt className={styles.detailTitle}>
            {headingTag ? (
              <Text tag="h3" bold>
                {shop.name}
              </Text>
            ) : (
              shop.name
            )}
          </dt>
          <dd className={styles.detailText}>
            <div className={styles.stationContainer}>
              <span className={styles.stationName}>{shop.closestStation}</span>
              <span>駅 徒歩{shop.closestStationTime}分</span>
            </div>
          </dd>
          {shop.shopRating?.shouldShowRating && (
            <dd className={styles.raging}>
              <ShopRating
                rating={shop.shopRating?.ratingCalculated}
                ratingCount={shop.shopRating?.ratingCount}
                shouldShowRating={true}
                rateCountSmall
              />
            </dd>
          )}
          {shop.campaignLabels.length > 0 && (
            <dd className={styles.campaignLabels}>
              <ShopCampaignLabel campaignLabels={shop.campaignLabels} />
            </dd>
          )}

          <dd className={styles.footer}>{Footer}</dd>
        </dl>
      </div>
    </div>
  );
};
